import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { CanvasSyncUpload, UploadStatus } from "@canvas-sync/shared";
import { api } from "../api/client";
import { CsvPreviewModal } from "../components/CsvPreviewModal";
import { UploadStatusBadge } from "../components/UploadStatusBadge";
import {
  CanvasResultCard,
  CanvasMessagesCard,
  CanvasStatusChecksCard,
} from "../components/CanvasResultCards";
import {
  formatDateTime,
  formatDuration,
  formatIdLabel,
  formatTime,
} from "../lib/format";
import { uploadTriggerLabel } from "../lib/labels";
import { useRole } from "../lib/roleContext";
import { POLL, usePoll } from "../lib/usePoll";

const TERMINAL_UPLOAD = new Set<UploadStatus>([
  "IMPORTED",
  "IMPORTED_WITH_MESSAGES",
  "FAILED",
  "FAILED_WITH_MESSAGES",
  "ABORTED",
  "ERROR",
]);

const PARTIAL_SUCCESS = new Set<UploadStatus>([
  "IMPORTED",
  "IMPORTED_WITH_MESSAGES",
]);

const PARTIAL_FAILURE = new Set<UploadStatus>([
  "FAILED",
  "FAILED_WITH_MESSAGES",
  "ABORTED",
  "ERROR",
]);

export function UploadDetailPage() {
  const { uploadId } = useParams<{ uploadId: string }>();
  const { isAdmin } = useRole();
  const nav = useNavigate();
  const [upload, setUpload] = useState<CanvasSyncUpload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadErr, setDownloadErr] = useState<string | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitErr, setResubmitErr] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  usePoll(
    async () => {
      if (!uploadId) return;
      try {
        const u = await api.getUpload(uploadId);
        setUpload(u);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    () =>
      upload && TERMINAL_UPLOAD.has(upload.status)
        ? POLL.IDLE_MS
        : POLL.ACTIVE_MS,
    [uploadId],
  );

  async function handleDownload() {
    if (!uploadId) return;
    setDownloadErr(null);
    try {
      const { url } = await api.getUploadCsvUrl(uploadId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setDownloadErr((e as Error).message);
    }
  }

  async function handlePreview() {
    if (!uploadId) return;
    setPreviewErr(null);
    setPreviewOpen(true);
    setPreviewUrl(null);
    try {
      const { url } = await api.getUploadCsvUrl(uploadId);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewErr((e as Error).message);
      setPreviewOpen(false);
    }
  }

  function closePreview() {
    setPreviewOpen(false);
    setPreviewUrl(null);
  }

  async function handleResubmit() {
    if (!uploadId) return;
    setResubmitErr(null);
    setResubmitting(true);
    try {
      const result = await api.resubmitUpload(uploadId);
      nav(`/uploads/${result.uploadId}`);
    } catch (e) {
      setResubmitErr((e as Error).message);
      setResubmitting(false);
    }
  }

  if (!uploadId) return <div>Missing uploadId</div>;

  const canvasImportUrl =
    upload?.canvasDomain && upload.sisImportId !== undefined
      ? `${upload.canvasDomain}/accounts/${upload.canvasAccountId}/sis_import/${upload.sisImportId}`
      : null;

  return (
    <section>
      <div className="page-head">
        <div>
          <div className="muted small">
            <Link to="/uploads" className="muted-link">
              All uploads
            </Link>
          </div>
          <h1>Upload from {formatIdLabel(uploadId)}</h1>
          <div className="muted small mono" style={{ marginTop: 4 }}>
            {uploadId}
          </div>
        </div>
        {upload && <UploadStatusBadge status={upload.status} />}
      </div>

      {error && <div className="error">{error}</div>}

      {upload && (
        <>
          <Lifecycle upload={upload} />

          <div className="meta-grid">
            <Meta
              label="Started by"
              value={
                <span className="trigger-pill">
                  {uploadTriggerLabel(upload.trigger)}
                </span>
              }
            />
            <Meta
              label="Canvas"
              value={
                <span className={`env-tag env-tag-${upload.canvasEnv}`}>
                  {upload.canvasEnv}
                </span>
              }
            />
            <Meta
              label="Canvas account ID"
              value={upload.canvasAccountId}
              mono
            />
            <Meta
              label="Canvas import ID"
              value={upload.sisImportId ?? "—"}
              mono
            />
            <Meta
              label="Canvas status"
              value={upload.workflowState ?? "—"}
              mono
            />
            <Meta label="Rows in CSV" value={upload.rowsSubmitted} mono />
            <Meta label="Created" value={formatDateTime(upload.queuedAt)} />
            <Meta
              label="Sent to Canvas"
              value={formatDateTime(upload.submittedAt)}
            />
            <Meta
              label="Canvas started processing"
              value={formatDateTime(upload.startedAt)}
            />
            <Meta
              label="Canvas finished"
              value={formatDateTime(upload.endedAt)}
            />
            <Meta
              label="Duration"
              value={formatDuration(upload.durationMs)}
              mono
            />
            <Meta
              label="Source file"
              value={shortS3(upload.sourceCsvS3Uri)}
              mono
              small
            />
            {upload.relatedRunId && (
              <Meta
                label="From sync"
                value={
                  <Link
                    to={`/syncs/${upload.relatedRunId}`}
                    className="muted-link"
                    title={upload.relatedRunId}
                  >
                    {formatIdLabel(upload.relatedRunId)}
                  </Link>
                }
              />
            )}
            {upload.rerunOfUploadId && (
              <Meta
                label="Resubmit of"
                value={
                  <Link
                    to={`/uploads/${upload.rerunOfUploadId}`}
                    className="muted-link"
                    title={upload.rerunOfUploadId}
                  >
                    {formatIdLabel(upload.rerunOfUploadId)}
                  </Link>
                }
              />
            )}
          </div>

          <CanvasResultCard
            counts={upload.canvasCounts}
            errorMessage={upload.errorMessage}
            note={
              upload.status === "IMPORTED"
                ? "Canvas accepted every row."
                : undefined
            }
            forceRender={
              PARTIAL_SUCCESS.has(upload.status) ||
              PARTIAL_FAILURE.has(upload.status)
            }
          />

          <CanvasMessagesCard
            warnings={upload.warnings}
            errors={upload.errors}
            sample={upload.processingErrorsSample}
          />

          <CanvasStatusChecksCard samples={upload.pollSamples} />

          <div className="actions">
            <button className="btn btn-primary" onClick={handleDownload}>
              Download submitted CSV
            </button>
            <button
              type="button"
              className="btn"
              onClick={handlePreview}
              title="Preview the submitted CSV inline (no download)"
              disabled={!upload.sourceCsvS3Uri}
            >
              Preview submitted CSV
            </button>
            {canvasImportUrl && (
              <a
                className="btn"
                href={canvasImportUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Canvas
              </a>
            )}
            {isAdmin && (
              <button
                className="btn"
                onClick={handleResubmit}
                disabled={resubmitting || !upload.sourceCsvS3Uri}
                title={
                  upload.sourceCsvS3Uri
                    ? "Send this CSV to Canvas again as a new upload"
                    : "The original CSV isn't available to resubmit"
                }
              >
                {resubmitting ? "Sending…" : "Send this CSV again"}
              </button>
            )}
            {upload.relatedRunId && (
              <Link to={`/syncs/${upload.relatedRunId}`} className="btn">
                Open the related sync
              </Link>
            )}
            <div className="muted small retention-note">
              Submitted CSVs are kept here permanently.
            </div>
            {downloadErr && <div className="error inline">{downloadErr}</div>}
            {resubmitErr && <div className="error inline">{resubmitErr}</div>}
            {previewErr && <div className="error inline">{previewErr}</div>}
          </div>
        </>
      )}

      {previewOpen && (
        <CsvPreviewModal
          title="Submitted CSV (sent to Canvas)"
          url={previewUrl}
          onClose={closePreview}
          onDownload={handleDownload}
        />
      )}
    </section>
  );
}

/** ── Lifecycle stepper ──────────────────────────────────────────────── */

type StepState = "done" | "active" | "pending" | "failed" | "skipped";

interface Step {
  label: string;
  detail?: string;
  state: StepState;
}

function Lifecycle({ upload }: { upload: CanvasSyncUpload }) {
  const steps: Step[] = lifecycleSteps(upload);
  return (
    <div className="card lifecycle">
      <ol className="lifecycle-steps">
        {steps.map((s, i) => (
          <li key={i} className={`lifecycle-step lifecycle-step-${s.state}`}>
            <span className="lifecycle-marker">
              {renderMarker(s.state, i + 1)}
            </span>
            <div className="lifecycle-text">
              <div className="lifecycle-label">{s.label}</div>
              {s.detail && <div className="lifecycle-detail">{s.detail}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function lifecycleSteps(u: CanvasSyncUpload): Step[] {
  const isError = u.status === "ERROR";
  const submittedAt = u.submittedAt;
  const started = u.startedAt;
  const ended = u.endedAt;

  // Step 1: CSV archived (set as soon as the upload row exists)
  const step1: Step = {
    label: "CSV prepared",
    detail: u.sourceCsvS3Uri ? `${u.rowsSubmitted} rows` : undefined,
    state: u.sourceCsvS3Uri ? "done" : "pending",
  };

  // Step 2: Submitted to Canvas
  const step2: Step = {
    label: "Sent to Canvas",
    detail: submittedAt ? formatDateTime(submittedAt) : undefined,
    state: isError
      ? "failed"
      : u.sisImportId !== undefined
        ? "done"
        : u.status === "SUBMITTING"
          ? "active"
          : "pending",
  };

  // Step 3: SIS import created
  const step3: Step = {
    label: "Canvas accepted the file",
    detail:
      u.sisImportId !== undefined
        ? `Canvas import ID: ${u.sisImportId}`
        : undefined,
    state: isError
      ? "skipped"
      : u.sisImportId !== undefined
        ? "done"
        : "pending",
  };

  // Step 4: Processing status checked
  const step4: Step = {
    label: "Canvas processing",
    detail: started ? `Started ${formatTime(started)}` : undefined,
    state: isError
      ? "skipped"
      : u.status === "IMPORTING"
        ? "active"
        : TERMINAL_UPLOAD.has(u.status) && u.status !== "ERROR"
          ? "done"
          : u.status === "SUBMITTED"
            ? "pending"
            : "pending",
  };

  // Step 5: Final result
  const finalDetail = ended ? formatDateTime(ended) : undefined;
  let finalState: StepState = "pending";
  if (u.status === "IMPORTED") finalState = "done";
  else if (u.status === "IMPORTED_WITH_MESSAGES") finalState = "done";
  else if (PARTIAL_FAILURE.has(u.status)) finalState = "failed";
  else if (TERMINAL_UPLOAD.has(u.status)) finalState = "done";
  const step5: Step = {
    label: "Final result",
    detail: finalDetail ?? statusBlurb(u.status),
    state: finalState,
  };

  return [step1, step2, step3, step4, step5];
}

function statusBlurb(s: UploadStatus): string {
  switch (s) {
    case "SUBMITTING":
      return "Preparing to send to Canvas…";
    case "SUBMITTED":
      return "Waiting for Canvas to start processing…";
    case "IMPORTING":
      return "Canvas is processing…";
    case "IMPORTED":
      return "Canvas imported the file successfully";
    case "IMPORTED_WITH_MESSAGES":
      return "Imported with warnings";
    case "FAILED":
      return "Canvas reported a failure";
    case "FAILED_WITH_MESSAGES":
      return "Some rows failed in Canvas";
    case "ABORTED":
      return "Canceled in Canvas";
    case "ERROR":
      return "Couldn't reach Canvas";
  }
}

function renderMarker(state: StepState, n: number) {
  if (state === "done") return n;
  if (state === "failed") return n;
  if (state === "skipped") return "—";
  if (state === "active") return <span className="lifecycle-spinner" />;
  return n;
}

/** ── Small bits ─────────────────────────────────────────────────────── */

function Meta({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: React.ReactNode | string | number | undefined;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div className="meta">
      <div className="meta-label">{label}</div>
      <div
        className={
          "meta-value" + (mono ? " mono" : "") + (small ? " small" : "")
        }
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function shortS3(uri: string | undefined): string {
  if (!uri) return "—";
  return uri.replace(/^s3:\/\/[^/]+\//, "");
}
